import React, { useState } from "react";
import Modal from "./Modal";
import { clusterApi, ApiError } from "../services/api";
import { VariantInfo } from "../types";
import { useClient } from "../contexts/ClientContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import "./VariantSelectionModal.css";

interface VariantSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  anchorKeyword: string;
  variants: VariantInfo[];
  onSuccess: (selected?: VariantInfo[]) => void;
  mode?: "create" | "expand";
  locationCode?: number;
  languageCode?: string;
}

const VariantSelectionModal: React.FC<VariantSelectionModalProps> = ({
  isOpen,
  onClose,
  anchorKeyword,
  variants,
  onSuccess,
  mode = "create",
  locationCode = 2840,
  languageCode = "en",
}) => {
  const { currentClient } = useClient();
  const [selectedVariants, setSelectedVariants] = useState<Set<VariantInfo>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Calculate tracking statistics
  const trackingStats = React.useMemo(() => {
    const tracked = variants.filter((v) => v.is_tracked).length;
    const untracked = variants.length - tracked;
    return { tracked, untracked };
  }, [variants]);

  const handleVariantToggle = (variantObj: VariantInfo) => {
    const newSelected = new Set(selectedVariants);
    // Check if variant is already selected by comparing variant strings
    const isSelected = Array.from(selectedVariants).some(
      (v) => v.variant === variantObj.variant
    );

    if (isSelected) {
      // Remove the variant by filtering out the matching one
      const filtered = Array.from(selectedVariants).filter(
        (v) => v.variant !== variantObj.variant
      );
      setSelectedVariants(new Set(filtered));
    } else {
      newSelected.add(variantObj);
      setSelectedVariants(newSelected);
    }
    if (error) setError(""); // Clear error when user makes changes
  };

  const handleSelectAll = () => {
    if (selectedVariants.size === variants.length) {
      // Deselect all
      setSelectedVariants(new Set());
    } else {
      // Select all variant objects
      setSelectedVariants(new Set(variants));
    }
  };

  const handleSelectTracked = () => {
    const trackedVariants = variants.filter((v) => v.is_tracked);
    setSelectedVariants(new Set(trackedVariants));
  };

  const handleSubmit = async () => {
    if (selectedVariants.size === 0) {
      setError("Please select at least one variant");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (mode === "create") {
        const response = await clusterApi.processSelectedVariants(
          currentClient,
          {
            anchor_keyword: anchorKeyword,
            variants: Array.from(selectedVariants),
            location_code: locationCode,
            language_code: languageCode,
          }
        );
        onSuccess();
      } else {
        onSuccess(Array.from(selectedVariants));
      }
      handleClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`API Error: ${err.message}`);
      } else {
        setError("Failed to process variants. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedVariants(new Set());
    setError("");
    setIsLoading(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Select Variants for "${anchorKeyword}"`}
      maxWidth="600px"
    >
      <div className="variant-selection-modal">
        <div className="modal-header-info">
          <p className="tracking-info">
            Select the variants you want to process for keyword monitoring
          </p>

          <div className="selection-controls">
            <button
              type="button"
              onClick={handleSelectAll}
              className="select-all-btn"
              disabled={isLoading}
            >
              {selectedVariants.size === variants.length
                ? "Deselect All"
                : "Select All"}
            </button>
          </div>
        </div>

        <div className="variants-list">
          {variants.map((variant, index) => (
            <div
              key={index}
              // className={`variant-item ${
              //   variant.is_tracked ? "tracked" : "untracked"
              // }`}
              className="variant-item"
            >
              <label className="variant-checkbox-container">
                <input
                  id={`variant-${index}`}
                  type="checkbox"
                  checked={Array.from(selectedVariants).some(
                    (v) => v.variant === variant.variant
                  )}
                  onChange={() => handleVariantToggle(variant)}
                  disabled={isLoading}
                />
                <span className="checkmark"></span>
                <div className="variant-content">
                  <span className="variant-text">{variant.variant}</span>
                </div>
              </label>
              <div className="relevance-score">
                {Math.round(
                  (variant.openai_similarity || variant.relevance_score) * 100
                )}
                %
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="error-message">
            {error} {" "}
            {error.includes("limit reached") && (
              <a
                href="/admin?tab=billing"
                className="underline hover:text-red-700 hover:font-semibold transition-all"
              >
                Upgrade your plan for more clusters.
              </a>
            )}
          </div>
        )}

        <div className="selection-summary">
          <span className="selected-count">
            {selectedVariants.size} of {variants.length} variants selected
          </span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isLoading || selectedVariants.size === 0}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner-button"></span>
                Processing...
              </>
            ) : (
              `Process ${selectedVariants.size} Variant${
                selectedVariants.size !== 1 ? "s" : ""
              }`
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VariantSelectionModal;
